import React from 'react';
import styled from 'styled-components';
import { User, BarChart3, TrendingUp } from 'lucide-react';
import { theme } from '../styles/theme';
import { InitialReview, ScoreReport } from '../types';
import InitialReviewCard from './InitialReviewCard';
import ScoreReportDisplay from './ScoreReportDisplay';

const AssessmentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
  margin-top: ${theme.spacing[4]};
`;

const OverviewSection = styled.div`
  background: linear-gradient(135deg, ${theme.colors.primary[50]}, ${theme.colors.accent[50]});
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[6]};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 100px;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.5));
    pointer-events: none;
  }
`;

const OverviewHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  margin-bottom: ${theme.spacing[4]};
`;

const OverviewIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, ${theme.colors.primary[500]}, ${theme.colors.accent[500]});
  border-radius: ${theme.borderRadius.md};
  color: white;
`;

const OverviewContent = styled.div`
  h2 {
    margin: 0;
    color: ${theme.colors.textPrimary};
    font-weight: ${theme.typography.fontWeight.bold};
    font-size: ${theme.typography.fontSize.xl};
  }

  p {
    margin: ${theme.spacing[1]} 0 0 0;
    color: ${theme.colors.textSecondary};
    font-size: ${theme.typography.fontSize.base};
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${theme.spacing[4]};
  position: relative;
  z-index: 1;
`;

const StatCard = styled.div`
  background: white;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing[4]};
  text-align: center;
  box-shadow: ${theme.shadows.sm};
  transition: all ${theme.transitions.base};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.md};
  }
`;

const StatValue = styled.div`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.primary[600]};
  margin-bottom: ${theme.spacing[1]};
`;

const StatLabel = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textSecondary};
  font-weight: ${theme.typography.fontWeight.medium};
`;

const StatIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: ${theme.colors.primary[100]};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.primary[600]};
  margin: 0 auto ${theme.spacing[2]} auto;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[10]};
  text-align: center;
  color: ${theme.colors.textMuted};
  background: ${theme.colors.backgroundSecondary};
  border: 2px dashed ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
`;

const EmptyIcon = styled.div`
  width: 64px;
  height: 64px;
  background: ${theme.colors.primary[100]};
  border-radius: ${theme.borderRadius.full};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${theme.spacing[4]};
  color: ${theme.colors.primary[500]};
`;

const EmptyTitle = styled.h3`
  margin: 0 0 ${theme.spacing[2]} 0;
  color: ${theme.colors.textPrimary};
  font-weight: ${theme.typography.fontWeight.semibold};
  font-size: ${theme.typography.fontSize.lg};
`;

const EmptyDescription = styled.p`
  margin: 0;
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.fontSize.sm};
  max-width: 400px;
  line-height: ${theme.typography.lineHeight.relaxed};
`;

const ProcessingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: ${theme.colors.primary[50]};
  border: 1px solid ${theme.colors.primary[200]};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.primary[700]};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  margin-bottom: ${theme.spacing[4]};
`;

const ProcessingDot = styled.div`
  width: 8px;
  height: 8px;
  background: ${theme.colors.primary[500]};
  border-radius: ${theme.borderRadius.full};
  animation: pulse 2s infinite;
`;

interface InitialAssessmentResultsProps {
  initialReview?: InitialReview | any;
  scoreReport?: ScoreReport | any;
  isProcessing?: boolean;
}

const InitialAssessmentResults: React.FC<InitialAssessmentResultsProps> = ({
  initialReview,
  scoreReport,
  isProcessing = false
}) => {
  const hasAnyResults = initialReview || scoreReport;

  // Data flow: the backend's create_initial_score_report node produces a
  // structured report (overall_score, executive_summary, detailed_analysis,
  // improvement_areas, competitive_positioning, section_scores reused
  // verbatim from the reviewer's weighted criteria). That object arrives as
  // the `scoreReport` prop. We render it via <ScoreReportDisplay/>; no
  // frontend LLM calls, no placeholder fallbacks — if the backend hasn't
  // produced a report yet, we just don't render that section.
  // All four stat values derive from backend-supplied data only — no
  // hardcoded fallbacks. If the data isn't there yet (initial render before
  // initial_reviewer_agent completes), the corresponding card is hidden so
  // the user never sees fabricated numbers.
  const overallScore: number | null =
    typeof scoreReport?.overall_score === 'number'
      ? scoreReport!.overall_score
      : typeof initialReview?.score === 'number'
        ? initialReview!.score
        : null;

  const grade: string | null =
    scoreReport?.grade ||
    (overallScore !== null
      ? overallScore >= 90 ? 'A'
        : overallScore >= 80 ? 'B'
        : overallScore >= 70 ? 'C'
        : overallScore >= 60 ? 'D'
        : 'F'
      : null);

  const sectionScores = scoreReport?.section_scores || [];
  const sectionsAnalyzed = sectionScores.length || null;

  const averageSectionScore: number | null = (() => {
    if (!sectionScores.length) return null;
    let total = 0;
    for (const s of sectionScores) {
      const max = (typeof s.max_score === 'number' && s.max_score > 0) ? s.max_score : 10;
      const sc = typeof s.score === 'number' ? s.score : 0;
      total += (sc / max) * 100;
    }
    return Math.round(total / sectionScores.length);
  })();

  if (!hasAnyResults && !isProcessing) {
    return (
      <AssessmentContainer>
        <EmptyState>
          <EmptyIcon>
            <TrendingUp size={32} />
          </EmptyIcon>
          <EmptyTitle>Initial Assessment Pending</EmptyTitle>
          <EmptyDescription>
            The AI reviewer will analyse your tender responses and generate a
            grounded assessment once document processing is complete.
          </EmptyDescription>
        </EmptyState>
      </AssessmentContainer>
    );
  }

  return (
    <AssessmentContainer>
      {isProcessing && (
        <ProcessingIndicator>
          <ProcessingDot />
          AI is analyzing your tender responses and generating assessment...
        </ProcessingIndicator>
      )}

      <OverviewSection>
        <OverviewHeader>
          <OverviewIcon>
            <TrendingUp size={24} />
          </OverviewIcon>
          <OverviewContent>
            <h2>Assessment Overview</h2>
            <p>Initial AI review and detailed scoring analysis</p>
          </OverviewContent>
        </OverviewHeader>

        <StatsGrid>
          {overallScore !== null && (
            <StatCard>
              <StatIcon>
                <User size={16} />
              </StatIcon>
              <StatValue>{overallScore}</StatValue>
              <StatLabel>Overall Score</StatLabel>
            </StatCard>
          )}

          {grade !== null && (
            <StatCard>
              <StatIcon>
                <TrendingUp size={16} />
              </StatIcon>
              <StatValue>{grade}</StatValue>
              <StatLabel>Grade</StatLabel>
            </StatCard>
          )}

          {sectionsAnalyzed !== null && (
            <StatCard>
              <StatIcon>
                <BarChart3 size={16} />
              </StatIcon>
              <StatValue>{sectionsAnalyzed}</StatValue>
              <StatLabel>Sections Analyzed</StatLabel>
            </StatCard>
          )}

          {averageSectionScore !== null && (
            <StatCard>
              <StatIcon>
                <TrendingUp size={16} />
              </StatIcon>
              <StatValue>{averageSectionScore}%</StatValue>
              <StatLabel>Average Section Score</StatLabel>
            </StatCard>
          )}
        </StatsGrid>
      </OverviewSection>

      {initialReview && <InitialReviewCard review={initialReview} />}

      {scoreReport && scoreReport.executive_summary ? (
        <ScoreReportDisplay scoreReport={scoreReport} />
      ) : null}
    </AssessmentContainer>
  );
};

export default InitialAssessmentResults;